import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  collection,
  where,
  getDocs,
  orderBy,
  limit,
  increment
} from 'firebase/firestore';
import { db } from './firebase';
import { generateRoomCode } from '../utils/generateCode';

/**
 * Session Service
 * Handles all session-related Firestore operations
 */

/**
 * Creates a new game session
 * @param {string} quizId - ID of the quiz to use
 * @returns {Promise<Object>} Created session with room code
 */
export const createSession = async (quizId) => {
  try {
    // Generate unique 6-character room code with collision check
    let roomCode;
    let sessionRef;
    const maxAttempts = 10;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      roomCode = generateRoomCode();
      const q = query(collection(db, 'sessions'), where('id', '==', roomCode));
      const existing = await getDocs(q);
      if (existing.empty) break;
      if (attempt === maxAttempts - 1) {
        throw new Error('Klarte ikkje generere unik PIN-kode. Prøv igjen.');
      }
    }
    sessionRef = doc(db, 'sessions', roomCode);

    const session = {
      id: roomCode,
      quizId: quizId,
      hostId: 'host', // MVP default
      status: 'waiting',
      currentQuestionIndex: -1, // -1 = not started
      questionStartTime: null,
      createdAt: new Date().toISOString(),
      participants: {}
    };

    await setDoc(sessionRef, session);

    return session;
  } catch (error) {
    console.error('Error creating session:', error);
    throw new Error(`Klarte ikkje opprette økt: ${error.message}`);
  }
};

/**
 * Starts a game session
 * @param {string} sessionId - Session ID (room code)
 * @returns {Promise<void>}
 */
export const startSession = async (sessionId) => {
  try {
    const sessionRef = doc(db, 'sessions', sessionId);

    await updateDoc(sessionRef, {
      status: 'active',
      currentQuestionIndex: 0,
      questionStartTime: new Date().toISOString()
    });

  } catch (error) {
    console.error('Error starting session:', error);
    throw new Error(`Klarte ikkje starte økt: ${error.message}`);
  }
};

/**
 * Advances to the next question
 * @param {string} sessionId - Session ID (room code)
 * @returns {Promise<void>}
 */
export const nextQuestion = async (sessionId) => {
  try {
    const sessionRef = doc(db, 'sessions', sessionId);
    const sessionSnap = await getDoc(sessionRef);

    if (!sessionSnap.exists()) {
      throw new Error('Fann ikkje økta');
    }

    const currentIndex = sessionSnap.data().currentQuestionIndex;

    await updateDoc(sessionRef, {
      currentQuestionIndex: currentIndex + 1,
      questionStartTime: new Date().toISOString()
    });

  } catch (error) {
    console.error('Error advancing to next question:', error);
    throw new Error(`Klarte ikkje gå til neste spørsmål: ${error.message}`);
  }
};

/**
 * Ends a game session
 * @param {string} sessionId - Session ID (room code)
 * @returns {Promise<void>}
 */
export const endSession = async (sessionId) => {
  try {
    const sessionRef = doc(db, 'sessions', sessionId);

    await updateDoc(sessionRef, {
      status: 'finished',
      finishedAt: new Date().toISOString()
    });

    // Increment quiz playCount
    const sessionSnap = await getDoc(sessionRef);
    if (sessionSnap.exists()) {
      const { quizId } = sessionSnap.data();
      if (quizId) {
        const quizRef = doc(db, 'quizzes', quizId);
        await updateDoc(quizRef, { playCount: increment(1) });
      }
    }

  } catch (error) {
    console.error('Error ending session:', error);
    throw new Error(`Klarte ikkje avslutte økt: ${error.message}`);
  }
};

/**
 * Fetches a session by room code
 * @param {string} sessionId - Session ID (room code)
 * @returns {Promise<Object>} Session data
 */
export const getSession = async (sessionId) => {
  try {
    const sessionRef = doc(db, 'sessions', sessionId);
    const sessionSnap = await getDoc(sessionRef);

    if (!sessionSnap.exists()) {
      throw new Error('Fann ikkje økta');
    }

    return sessionSnap.data();
  } catch (error) {
    console.error('Error fetching session:', error);
    throw new Error(`Klarte ikkje hente økt: ${error.message}`);
  }
};

/**
 * Fetches recently finished sessions
 * @param {number} maxResults - Maximum number of sessions to return
 * @returns {Promise<Array>} Array of finished session objects
 */
export const getFinishedSessions = async (maxResults = 10) => {
  try {
    const q = query(
      collection(db, 'sessions'),
      where('status', '==', 'finished'),
      orderBy('createdAt', 'desc'),
      limit(maxResults)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching finished sessions:', error);
    return [];
  }
};
